import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  exclusiveEndToLastDay,
  formatCalendarDate,
  instantToDate,
  isDateAfter,
} from "../../utils/wage-dates";
import {
  listWageLines,
  type OrganizationWageSetWages,
} from "../../utils/wage-fields";

type VersionRow = {
  version: {
    id: string;
    effectiveStartsAt: string | Date;
    effectiveEndsAt: string | Date | null;
  };
  wages: OrganizationWageSetWages;
};

type VersionHistoryProps = {
  versions: VersionRow[];
  retiredAt: string | Date | null;
  timezoneId?: string;
  today: string;
};

function describeWindow({
  row,
  retiredAt,
  timezoneId,
}: {
  row: VersionRow;
  retiredAt: string | Date | null;
  timezoneId?: string;
}): string {
  const from = formatCalendarDate(
    instantToDate({ instant: row.version.effectiveStartsAt, timezoneId }),
  );
  const end = row.version.effectiveEndsAt ?? retiredAt;
  if (!end) {
    return `From ${from}`;
  }
  const through = formatCalendarDate(
    exclusiveEndToLastDay({ instant: end, timezoneId }),
  );
  return `From ${from} through ${through}`;
}

export function VersionHistory({
  versions,
  retiredAt,
  timezoneId,
  today,
}: VersionHistoryProps) {
  return (
    <Card className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">Version history</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {versions.map((row) => {
            const startsOn = instantToDate({
              instant: row.version.effectiveStartsAt,
              timezoneId,
            });
            const isUpcoming = isDateAfter({ date: startsOn, other: today });
            return (
              <li
                key={row.version.id}
                className="space-y-2 border-b border-slate-100 pb-4 last:border-b-0 last:pb-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">
                    {describeWindow({ row, retiredAt, timezoneId })}
                  </span>
                  {isUpcoming && <Badge variant="secondary">Upcoming</Badge>}
                </div>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  {listWageLines(row.wages).map((line) => (
                    <div key={line.key} className="flex justify-between gap-4">
                      <dt className="text-slate-600">{line.label}</dt>
                      <dd className="font-medium text-slate-900">
                        {line.display}
                      </dd>
                    </div>
                  ))}
                </dl>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
